import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import { Card } from '@crave/ui';
import { api } from '../api.ts';
import { Notice, Section, Status, errorText, money, when } from '../components/ui.tsx';
import { useStaff } from '../lib/staff.tsx';
import {
  groupSteps,
  personLabel,
  plainSummary,
  primaryOf,
  shortWhen,
  type AuditItem,
} from './AuditTrail.tsx';

interface Dash {
  date: string;
  ordering: { enabled: boolean; paused: boolean; blockers: { message: string }[] };
  by_status: { status: string; n: number; units: number }[];
  money: { gross: number; tips: number; captured: number; refunded: number; comps: number } | null;
  upcoming: { service_date: string; orders: number; units: number }[];
  attention: {
    id: string;
    order_number: string;
    status: string;
    review_reason: string | null;
    pickup_at: string;
  }[];
  outbox_problems: number;
}

/** Orders being made right now, including remakes and orders a guest asked to cancel. */
const LIVE = ['QUEUED', 'PREPARING', 'IN_OVEN', 'BOXING', 'READY', 'REMAKE', 'CANCEL_REQUESTED'];
/** Orders handed over, including ones refunded afterwards. */
const DONE = ['COMPLETED', 'PICKED_UP', 'PARTIALLY_REFUNDED', 'REFUNDED'];
/** Paid orders not yet in the kitchen. */
const WAITING = ['CONFIRMED', 'SCHEDULED', 'MANUAL_REVIEW'];

const LONG_DAY = new Intl.DateTimeFormat('en-US', {
  weekday: 'long',
  month: 'long',
  day: 'numeric',
  timeZone: 'UTC',
});
const SHORT_DAY = new Intl.DateTimeFormat('en-US', {
  weekday: 'short',
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
});

/** A service date (YYYY-MM-DD) in words, or the text as it is when it is not one. */
function dayWords(date: string, format: Intl.DateTimeFormat): string {
  const d = new Date(`${date}T12:00:00Z`);
  return /^\d{4}-\d{2}-\d{2}$/.test(date) && !Number.isNaN(d.getTime()) ? format.format(d) : date;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export function Dashboard() {
  const { can, location } = useStaff();
  const canAudit = can('audit.read');
  const canOutbox = can('settings.manage');
  const q = useQuery({
    queryKey: ['dashboard', location.slug],
    queryFn: async () =>
      (await api.get<Dash>(`/admin/reports/dashboard?location=${location.slug}`)).data,
    refetchInterval: 15_000,
  });
  if (q.isError) return <Notice kind="error">Could not load the dashboard.</Notice>;
  if (!q.data) return <p>Loading…</p>;
  const d = q.data;
  const count = (s: string) => d.by_status.find((x) => x.status === s)?.n ?? 0;
  const sum = (statuses: string[]) => statuses.reduce((s, x) => s + count(x), 0);
  const live = sum(LIVE);
  const completed = sum(DONE);
  const confirmed = sum(WAITING) + live + completed;
  return (
    <Section title={`Today, ${dayWords(d.date, LONG_DAY)}`}>
      {!d.ordering.enabled && (
        <Notice kind="error">
          Online ordering is off.{' '}
          {d.ordering.blockers.length ? (
            <>
              {plural(d.ordering.blockers.length, 'thing', 'things')} to fix under{' '}
              <Link to="/readiness">Menu, prices & hours</Link>.
            </>
          ) : (
            <>
              Turn it on from <Link to="/readiness">Menu, prices & hours</Link> when you are ready.
            </>
          )}
        </Notice>
      )}
      {d.ordering.paused && <Notice kind="error">Ordering is paused.</Notice>}
      {d.outbox_problems > 0 && (
        <Notice kind="error">
          {plural(d.outbox_problems, 'background task', 'background tasks')} (customer emails or
          Stripe clean-up) did not go through yet.{' '}
          {canOutbox ? (
            <Link to="/settings/outbox">Open Outbox</Link>
          ) : (
            'Ask the owner to open Outbox.'
          )}
        </Notice>
      )}
      <div
        style={{
          display: 'grid',
          // At most four tiles a row (two rows of four on desktop), never narrower than 150px.
          gridTemplateColumns:
            'repeat(auto-fit, minmax(min(100%, max(150px, calc((100% - 36px) / 4))), 1fr))',
          gap: 12,
        }}
      >
        <Stat label="Confirmed today" value={String(confirmed)} />
        <Stat label="In the kitchen" value={String(live)} />
        <Stat label="Completed" value={String(completed)} />
        <Stat label="No-shows / canceled" value={`${count('NO_SHOW')} / ${count('CANCELED')}`} />
        {d.money && <Stat label="Captured" value={money(d.money.captured)} />}
        {d.money && <Stat label="Tips" value={money(d.money.tips)} />}
        {d.money && <Stat label="Comps" value={money(d.money.comps)} />}
        {d.money && <Stat label="Refunded" value={money(d.money.refunded)} />}
      </div>
      {d.attention.length > 0 && (
        <Card>
          <strong>Needs a human</strong>
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {d.attention.map((a) => (
              <li key={a.id}>
                <Link to={`/orders/${a.id}`}>{a.order_number}</Link> · <Status s={a.status} /> ·
                pickup {when(a.pickup_at, 'Never')}
                {a.review_reason ? ` · ${a.review_reason}` : ''}
              </li>
            ))}
          </ul>
        </Card>
      )}
      <Card>
        <strong>Coming up</strong>
        {d.upcoming.length === 0 ? (
          <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)' }}>No future orders yet.</p>
        ) : (
          <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
            {d.upcoming.map((u) => (
              <li key={u.service_date}>
                {dayWords(u.service_date, SHORT_DAY)}: {plural(u.orders, 'order', 'orders')} ·{' '}
                {plural(u.units, 'pizza', 'pizzas')}
              </li>
            ))}
          </ul>
        )}
      </Card>
      {canAudit && <RecentChanges />}
    </Section>
  );
}

/**
 * The last five things people on the team did, from the audit trail. Changes made by one press of
 * a button (an order moving on and the stock it used) count as one, named by the main change.
 */
function RecentChanges() {
  const q = useQuery({
    queryKey: ['audit-recent'],
    queryFn: async () =>
      (await api.get<{ items: AuditItem[] }>('/admin/audit?actor_type=STAFF&limit=60')).data.items,
    refetchInterval: 60_000,
  });
  const steps = groupSteps(q.data ?? [])
    .slice(0, 5)
    .map((items) => ({ primary: primaryOf(items), others: items.length - 1 }));
  return (
    <Card>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: '4px 12px',
        }}
      >
        <strong>Recent changes</strong>
        <Link
          to="/audit"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            minHeight: 'var(--touch)',
            fontSize: 14,
            fontWeight: 600,
            color: 'var(--ink)',
          }}
        >
          Open the audit trail
        </Link>
      </div>
      {q.isError ? (
        <p style={{ margin: '8px 0 0', color: 'var(--crave-red)' }}>
          {errorText(q.error, 'Could not load recent changes.')}
        </p>
      ) : !q.data ? (
        <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)' }}>Loading…</p>
      ) : steps.length === 0 ? (
        <p style={{ margin: '8px 0 0', color: 'var(--ink-soft)' }}>No changes by the team yet.</p>
      ) : (
        <ul style={{ listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
          {steps.map(({ primary, others }) => (
            <li
              key={primary.id}
              style={{ padding: '8px 0', borderTop: '1px solid var(--line)', minWidth: 0 }}
            >
              <Link
                to={`/audit?range=all&entry=${encodeURIComponent(primary.id)}`}
                style={{ color: 'var(--ink)', overflowWrap: 'anywhere' }}
              >
                {plainSummary(primary)}
              </Link>
              <div style={{ fontSize: 13, color: 'var(--ink-soft)', marginTop: 2 }}>
                {shortWhen(primary.local_time)} · {personLabel(primary.actor)}
                {others > 0 ? ` · ${plural(others, 'other change', 'other changes')} with it` : ''}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <Card
      style={{
        padding: 16,
        minWidth: 0,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        gap: 4,
      }}
    >
      <div style={{ fontSize: 13, lineHeight: 1.3, color: 'var(--ink-soft)' }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, lineHeight: 1.2, overflowWrap: 'anywhere' }}>
        {value}
      </div>
    </Card>
  );
}
